import React, { useState, useEffect } from 'react'
import { CourseCard } from '../../CourseCard'
import { SectionGrid } from '../SectionGrid'
import { coursesAPI } from '../../../api/courses'
import { calcDurationMinutes, formatDuration } from '../../../utils/duration'
import type { Course } from '../../../types/api'

interface AvailableCoursesProps {
  limit?: number
  onBrowse?: () => void
  onProgressCalculated?: (pct: number) => void
}

export const AvailableCourses: React.FC<AvailableCoursesProps> = ({ limit, onBrowse, onProgressCalculated }) => {
  const [courses, setCourses] = useState<Course[]>([])
  const [courseDurations, setCourseDurations] = useState<Record<number, string>>({})
  const [courseProgressMap, setCourseProgressMap] = useState<Record<number, number>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data: Course[] = await coursesAPI.getAllCourses()
        setCourses(data)

        const results = await Promise.all(
          data.map((course) =>
            coursesAPI
              .getCourseModules(String(course.id))
              .then((modules: Array<{ total_exercises?: number; progress?: number }>) => {
                const totalExercises = modules.reduce((sum, m) => sum + (m.total_exercises ?? 0), 0)
                const courseProgress = modules.length > 0
                  ? Math.round(modules.reduce((sum, m) => sum + (m.progress ?? 0), 0) / modules.length)
                  : 0
                return { id: course.id, duration: formatDuration(calcDurationMinutes(totalExercises)), progress: courseProgress }
              })
              .catch(() => ({ id: course.id, duration: '', progress: 0 }))
          )
        )

        const durationMap: Record<number, string> = {}
        const progressMap: Record<number, number> = {}
        results.forEach(({ id, duration, progress }) => {
          durationMap[id] = duration
          progressMap[id] = progress
        })
        setCourseDurations(durationMap)
        setCourseProgressMap(progressMap)

        const allProgress = Object.values(progressMap)
        const overallPct = allProgress.length > 0
          ? Math.round(allProgress.reduce((sum, p) => sum + p, 0) / allProgress.length)
          : 0
        onProgressCalculated?.(overallPct)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load courses')
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const displayed = limit ? courses.slice(0, limit) : courses

  return (
    <SectionGrid
      title="Available Courses"
      subtitle="Explore latest learning paths curated for your role."
      loading={loading}
      error={error}
      emptyText="No courses available"
      loadingText="Loading courses..."
      gridClassName="flex flex-col"
      rightContent={
        onBrowse ? (
          <button
            onClick={onBrowse}
            className="px-5 py-2.5 text-sm font-bold bg-[#7a6020] text-white rounded-xl hover:opacity-90 transition-colors"
          >
            Browse Full Catalog
          </button>
        ) : undefined
      }
    >
      {displayed.map((course) => (
        <CourseCard
          key={course.id}
          course={course}
          computedDuration={courseDurations[course.id]}
          labProgress={courseProgressMap[course.id]}
        />
      ))}
    </SectionGrid>
  )
}